import React from 'react';
import { useTranslation } from 'react-i18next';
import { isBefore } from 'date-fns';

import ContractDetails from './ContractDetails';
import SubMenu from '../Common/SubMenu';
import Box from '../Common/Box';
import Accordion from '../Common/Accordion';
import Loading from '../Common/Loading';

// Utils.
import { contractRename } from '../../utils/contracts';
import { gaPageviewIfAllowed } from '../../utils/googleAnalytics';
import { OmaLummeApp } from '../../omaLummeContext';

/**
 * Contracts page. Displays a list of contracts.
 */
const Contracts: React.FunctionComponent = () => {
  const { t } = useTranslation();

  const { users, basicData, loadingBasicData } = OmaLummeApp.useState();

  const { customerType } = users.activeUser;
  const [pageview, setPageview] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!pageview) {
      gaPageviewIfAllowed('/contracts');
      setPageview(true);
    }
  }, [pageview]);

  const contractGroups: { [key: string]: Array<React.JSX.Element> } = {};
  const expiredContractGroups: { [key: string]: Array<React.JSX.Element> } = {};
  const groupedContracts: Array<React.JSX.Element> = [];
  const expiredGroupedContracts: Array<React.JSX.Element> = [];

  if (basicData) {
    for (const contract of basicData) {
      const contractGroup = t(`contracts:contracts.contractTypeNo${contract.contractTypeNo}`);

      const status = contract.contractStatus.toLowerCase();

      if (status === 's' || (status === 'o' && isBefore(new Date(contract.contractEndDate), new Date()))) {
        if (!expiredContractGroups[contractGroup]) {
          expiredContractGroups[contractGroup] = [];
        }
        expiredContractGroups[contractGroup].push(
          <ContractDetails key={contract.contractCode} contractBasicData={contract} onlyContract={basicData.length === 1} />,
        );
      } else {
        if (!contractGroups[contractGroup]) {
          contractGroups[contractGroup] = [];
        }
        contractGroups[contractGroup].push(
          <ContractDetails key={contract.contractCode} contractBasicData={contract} onlyContract={basicData.length === 1} />,
        );
      }
    }

    for (const groupName in contractGroups) {
      const groupTitle = contractRename(groupName);

      let accordionClass = 'contracts';

      if (groupName === t('contracts:contracts.contractTypeNo10')) {
        accordionClass = 'contractsOther';
      }
      if (groupName === t('contracts:contracts.contractTypeNo26')) {
        accordionClass = 'contractsEV';
      }

      const singleGroup = (
        <Accordion
          className={accordionClass}
          icon={true}
          key={groupTitle}
          title={groupTitle}
          open={Object.keys(contractGroups).length === 1 && contractGroups[groupName].length === 1}
        >
          {contractGroups[groupName]}
        </Accordion>
      );
      groupedContracts.push(singleGroup);
    }
    for (const groupName in expiredContractGroups) {
      const groupTitle = contractRename(groupName);

      let accordionClass = 'contractsExpired';

      if (groupName === t('contracts:contracts.contractTypeNo10')) {
        accordionClass = 'contractsOtherExpired';
      }
      if (groupName === t('contracts:contracts.contractTypeNo26')) {
        accordionClass = 'contractsEVExpired';
      }

      const singleGroup = (
        <Accordion
          className={accordionClass}
          icon={true}
          key={groupTitle}
          title={groupTitle}
          open={
            Object.keys(contractGroups).length === 0 &&
            Object.keys(expiredContractGroups).length === 1 &&
            expiredContractGroups[groupName].length === 1
          }
        >
          {expiredContractGroups[groupName]}
        </Accordion>
      );
      expiredGroupedContracts.push(singleGroup);
    }
  }

  return (
    <Box>
      <h1 aria-live="polite">{t('contracts:contracts.activeContractsTitle')}</h1>
      {customerType !== 'business' && users.activeUser.userId !== 0 && <SubMenu />}
      <p>
        {t('contracts:contracts.contractsIngress')}
        <a
          href="#choose-customership"
          onClick={(e): void => {
            e.preventDefault();
            document.getElementById('choose-customership')!.focus();
          }}
        >
          {t('contracts:contracts.contractsIngressLink')}
        </a>
        .
      </p>
      {!loadingBasicData && (!basicData || (basicData && basicData.length === 0)) && (
        <p>
          <strong>{t(`common:general.contractsOrMeteringPointsNotFound`)}</strong>
        </p>
      )}
      {groupedContracts}
      {expiredGroupedContracts.length > 0 && (
        <>
          <h2 className="h4" aria-live="polite">
            {t('contracts:contracts.expiredContractsTitle')}
          </h2>
          {expiredGroupedContracts}
        </>
      )}
      {users.activeUser.userId !== 0 && !basicData && <Loading />}
    </Box>
  );
};

export default Contracts;
